import React, { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { setSentryUser } from '@/lib/sentry';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  profileLoading: boolean;
  profileReady: boolean;
  profileError: string | null;
  organizationId: string | null;
  viewAllAppointments: boolean;
  signOut: () => Promise<void>;
  refreshUserData: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface UserProfileData {
  organization_id: string | null;
  view_all_appointments: boolean;
}

interface ProfileRpcResponse {
  data: unknown;
  error: { message?: string } | null;
}

interface FetchProfileOptions {
  logErrors?: boolean;
}

const PROFILE_CACHE_KEY = 'medicert.auth.profile';
const PROFILE_FETCH_TIMEOUT_MS = 5000;
const PROFILE_FETCH_ATTEMPTS = 4;
const PROFILE_RETRY_DELAYS_MS = [500, 1500, 3000];
const PROFILE_CACHE_MAX_AGE_MS = 10 * 60 * 1000;
const PROFILE_BACKGROUND_REFRESH_COOLDOWN_MS = 60 * 1000;

interface CachedProfileData {
  profile: UserProfileData;
  cachedAt: number;
}

const isProfileFetchTimeout = (error: unknown) => {
  if (!(error instanceof Error)) return false;
  return (
    error.name === 'AbortError' ||
    error.message === 'Profile fetch timeout' ||
    error.message.includes('aborted')
  );
};

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const readCachedProfile = (userId: string): CachedProfileData | null => {
  try {
    const cached = localStorage.getItem(PROFILE_CACHE_KEY);
    if (!cached) return null;
    const parsed = JSON.parse(cached);
    if (parsed?.userId !== userId) return null;
    const profile = toUserProfileData(parsed.profile);
    if (!profile) return null;
    return {
      profile,
      cachedAt: typeof parsed.cachedAt === 'number' ? parsed.cachedAt : Date.now(),
    };
  } catch {
    return null;
  }
};

const writeCachedProfile = (userId: string, profile: UserProfileData) => {
  localStorage.setItem(
    PROFILE_CACHE_KEY,
    JSON.stringify({ userId, profile, cachedAt: Date.now() })
  );
};

const isCachedProfileFresh = (cachedProfile: CachedProfileData) =>
  Date.now() - cachedProfile.cachedAt < PROFILE_CACHE_MAX_AGE_MS;

const isBackgroundRefreshAllowed = (lastRefreshAt: number) =>
  Date.now() - lastRefreshAt > PROFILE_BACKGROUND_REFRESH_COOLDOWN_MS;

const toUserProfileData = (profile: unknown): UserProfileData | null => {
  if (!profile || typeof profile !== 'object') return null;

  const rawProfile = profile as Record<string, unknown>;
  return {
    organization_id:
      typeof rawProfile.organization_id === 'string' ? rawProfile.organization_id : null,
    view_all_appointments: rawProfile.view_all_appointments === true,
  };
};

const fetchUserProfile = async (
  userId: string,
  options: FetchProfileOptions = {}
): Promise<UserProfileData | null> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), PROFILE_FETCH_TIMEOUT_MS);

  try {
    const profileRequest = supabase.rpc('get_user_profile', { user_id: userId }) as unknown as {
      abortSignal: (signal: AbortSignal) => PromiseLike<ProfileRpcResponse>;
    };
    const { data: profile, error } = await profileRequest.abortSignal(controller.signal);

    if (error) {
      if (options.logErrors) console.error('Error fetching profile:', error);
      return null;
    }

    if (!profile) {
      return { organization_id: null, view_all_appointments: false };
    }

    return toUserProfileData(profile);
  } catch (error) {
    if (!isProfileFetchTimeout(error) && options.logErrors) {
      console.error('Error in fetchUserProfile:', error);
    }
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
};

const fetchUserProfileWithRetry = async (
  userId: string,
  options: FetchProfileOptions = {}
): Promise<UserProfileData | null> => {
  for (let attempt = 0; attempt < PROFILE_FETCH_ATTEMPTS; attempt += 1) {
    const profile = await fetchUserProfile(userId, options);
    if (profile) return profile;

    if (attempt < PROFILE_FETCH_ATTEMPTS - 1) {
      await wait(PROFILE_RETRY_DELAYS_MS[Math.min(attempt, PROFILE_RETRY_DELAYS_MS.length - 1)]);
    }
  }

  return null;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileReady, setProfileReady] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [viewAllAppointments, setViewAllAppointments] = useState(false);
  const activeProfileFetchId = useRef(0);
  const lastProfileRefreshAt = useRef(0);

  const applyProfile = (profile: UserProfileData) => {
    setOrganizationId(profile.organization_id);
    setViewAllAppointments(profile.view_all_appointments);
  };

  const refreshUserData = async () => {
    if (!user?.id) return;

    try {
      console.log('Manually refreshing organization data for user:', user.id);
      setProfileLoading(true);
      setProfileError(null);
      const profile = await fetchUserProfileWithRetry(user.id, { logErrors: true });
      if (!profile) {
        setProfileError('Nie udało się pobrać profilu. Sprawdź połączenie i spróbuj ponownie.');
        return;
      }
      applyProfile(profile);
      if (profile.organization_id) writeCachedProfile(user.id, profile);
      setProfileReady(true);
      console.log('Organization data updated:', profile.organization_id);
    } catch (error) {
      console.error('Error refreshing organization data:', error);
      setProfileError('Nie udało się pobrać profilu. Sprawdź połączenie i spróbuj ponownie.');
    } finally {
      setProfileLoading(false);
    }
  };

  useEffect(() => {
    console.log('AuthProvider initializing...');

    let isMounted = true;
    let loadingTimeout: ReturnType<typeof setTimeout> | null = null;

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, currentSession) => {
      console.log('Auth state changed:', event);

      if (!isMounted) return;
      if (loadingTimeout) {
        clearTimeout(loadingTimeout);
        loadingTimeout = null;
      }

      if (event === 'SIGNED_OUT') {
        activeProfileFetchId.current += 1;
        setSession(null);
        setUser(null);
        setOrganizationId(null);
        setViewAllAppointments(false);
        setProfileReady(false);
        setProfileError(null);
        setProfileLoading(false);
        setLoading(false);
        setSentryUser(null);

        localStorage.removeItem('supabase.auth.token');
        sessionStorage.removeItem('supabase.auth.token');
        localStorage.removeItem(PROFILE_CACHE_KEY);
        return;
      }

      // INITIAL_SESSION, SIGNED_IN, TOKEN_REFRESHED, USER_UPDATED, etc.
      setSession(currentSession);
      setUser(currentSession?.user ?? null);
      setLoading(false);
      if (currentSession?.user) {
        setSentryUser({ id: currentSession.user.id, email: currentSession.user.email });
      }

      if (currentSession?.user) {
        const fetchId = activeProfileFetchId.current + 1;
        activeProfileFetchId.current = fetchId;
        const cachedProfile = readCachedProfile(currentSession.user.id);
        const hasCache = !!cachedProfile;
        const shouldRefreshProfile =
          !cachedProfile ||
          (!isCachedProfileFresh(cachedProfile) &&
            isBackgroundRefreshAllowed(lastProfileRefreshAt.current));
        if (cachedProfile) {
          applyProfile(cachedProfile.profile);
          setProfileReady(true);
          setProfileError(null);
          setProfileLoading(false);
        } else {
          setProfileReady(false);
          setProfileError(null);
          setProfileLoading(true);
        }

        if (!shouldRefreshProfile) return;

        lastProfileRefreshAt.current = Date.now();

        // Defer profile fetch — never block UI
        setTimeout(() => {
          if (!isMounted) return;
          fetchUserProfileWithRetry(currentSession.user.id, { logErrors: !hasCache })
            .then(profile => {
              if (!isMounted || activeProfileFetchId.current !== fetchId) return;
              if (!profile) {
                // If we already have cached data showing, don't surface an error —
                // keep the user productive and retry silently on next auth event.
                if (!hasCache) {
                  setProfileError(
                    'Nie udało się pobrać profilu. Sprawdź połączenie i spróbuj ponownie.'
                  );
                }
                return;
              }
              applyProfile(profile);
              if (profile.organization_id) writeCachedProfile(currentSession.user.id, profile);
              setProfileReady(true);
              setProfileError(null);
            })
            .catch(error => {
              console.error('Error updating organization data:', error);
              if (isMounted && activeProfileFetchId.current === fetchId && !hasCache) {
                setProfileError(
                  'Nie udało się pobrać profilu. Sprawdź połączenie i spróbuj ponownie.'
                );
              }
            })
            .finally(() => {
              if (isMounted && activeProfileFetchId.current === fetchId) setProfileLoading(false);
            });
        }, 0);
      } else {
        activeProfileFetchId.current += 1;
        setOrganizationId(null);
        setViewAllAppointments(false);
        setProfileReady(false);
        setProfileError(null);
        setProfileLoading(false);
      }
    });

    // Safety net: if onAuthStateChange doesn't fire within 1.5s (e.g. network stall),
    // stop showing the loading spinner so the login page becomes interactive.
    loadingTimeout = setTimeout(() => {
      if (isMounted) {
        console.warn('Auth init timeout — releasing loading state');
        setLoading(false);
      }
    }, 1500);

    // Trigger session restore in the background; the listener above handles the result.
    supabase.auth.getSession().catch(error => {
      console.error('Error initializing auth:', error);
      if (isMounted) setLoading(false);
    });

    return () => {
      isMounted = false;
      if (loadingTimeout) clearTimeout(loadingTimeout);
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    setLoading(true);
    try {
      console.log('Signing out user...');

      localStorage.removeItem('supabase.auth.token');
      sessionStorage.removeItem('supabase.auth.token');

      const { error } = await supabase.auth.signOut({ scope: 'global' });
      if (error) {
        toast.error(`Błąd wylogowania: ${error.message}`);
        console.error('Error signing out:', error);
      } else {
        setOrganizationId(null);
        setViewAllAppointments(false);
        setProfileReady(false);
        setProfileError(null);
        setUser(null);
        setSession(null);
        toast.success('Wylogowano pomyślnie');
      }
    } catch (error: unknown) {
      console.error('Exception during sign out:', error);
      const message = error instanceof Error ? error.message : 'Nieznany błąd';
      toast.error(`Wystąpił błąd: ${message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        profileLoading,
        profileReady,
        profileError,
        organizationId,
        viewAllAppointments,
        signOut,
        refreshUserData,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
